import classNames from 'classnames';
import React, { ChangeEvent, useState } from 'react';

import { Button, LinkButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { IconTypes } from '@eika/icon';
import { InputLabel, SearchInput } from '@eika/input';
import { DarkSkeleton } from '@eika/skeleton';
import { Tabs } from '@eika/tabs';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { CrossBorderReceiversDetails } from '../../components/CrossBorderReceiversDetails';
import { EikaModal } from '../../components/EikaModal';
import { ReceiverDetails } from '../../components/ReceiverDetails';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { SelectList } from '../../components/shared/SelectList';
import CreateNewCrossBorderReceiver from '../../components/transaction/CrossBorderPayment/Receiver/CreateNewCrossBorderReceiver';
import crossBorderPaymentReducer from '../../components/transaction/CrossBorderPayment/form/reducer';
import { crossBorderPaymentInitialState } from '../../components/transaction/CrossBorderPayment/form/state';
import { CrossBorderFormContext } from '../../components/transaction/CrossBorderPayment/form/useFormContext';
import useValidate from '../../components/transaction/CrossBorderPayment/form/validate';
import NewReceiver from '../../components/transaction/Payment/SelectReceiver/NewReceiver';
import ReceiverListItem from '../../components/transaction/Payment/SelectReceiver/ReceiverListItem';
import { useConfig } from '../../defaultConfig';
import { useCrossBorderReceivers, useRefreshCrossBorderReceivers } from '../../fetchHooks/useCrossBorderReceivers';
import useReceivers, { useMutateReceivers } from '../../fetchHooks/useReceivers';
import type { CrossBorderReceiver, Receiver } from '../../generated-types';
import useReceiverElements from '../../hooks/useReceiverElements';
import useTranslation, { format } from '../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { ReceiverElement, ReceiverType } from './ReceiverElement';
import ReceiversSkeleton from './ReceiversSkeleton';

import './Receivers.scss';

enum ReceiverTypes {
    NORWEGIAN = 'NORWEGIAN',
    CROSS_BORDER = 'CROSS_BORDER',
}

const Receivers: React.FC = () => {
    const translated = useTranslation();
    const { dispatch } = useSigningContext();
    const { featureToggles } = useConfig();
    const [searchString, setSearchString] = useState<string>('');
    const [editDone, setEditDone] = useState(false);
    const [editCrossBorderReceiver, setEditCrossBorderReceiver] = useState(false);
    const [newReceiverModalIsOpen, setNewReceiverModalIsOpen] = useState(false);
    const [receiverDetailsModalIsOpen, setReceiverDetailsModalIsOpen] = useState(false);
    const [crossBorderReceiverModalIsOpen, setCrossBorderReceiverModalIsOpen] = useState(false);
    const [chosenReceiver, setChosenReceiver] = useState<Receiver>();
    const [chosenCrossBorderReceiver, setChosenCrossBorderReceiver] = useState<CrossBorderReceiver>();
    const [showSuccess, setShowSuccess] = useState(false);

    const { data: receivers = [], ...receiverQuery } = useReceivers();
    const { data: crossBorderReceivers = [], ...crossBorderReceiverQuery } = useCrossBorderReceivers(
        featureToggles.enableCreateCrossBorderReceiver,
    );

    const receiverItems = useReceiverElements(receivers, crossBorderReceivers);

    const { invalidate } = useMutateReceivers();
    const refreshCrossBorderReceivers = useRefreshCrossBorderReceivers();

    const openAddNewReceiverModal = () => {
        setNewReceiverModalIsOpen(true);
    };
    const filteredReceivers =
        receiverItems?.filter((account) => {
            const searchStringWithoutSeparators = searchString.replace(/[\s.]+/g, '');
            return (
                account.accountNumber.value.includes(searchStringWithoutSeparators) ||
                account.name.toLowerCase().includes(searchString.toLowerCase())
            );
        }) ?? [];
    const closeNewReceiverModal = () => {
        setShowSuccess(false);
        invalidate();
        refreshCrossBorderReceivers();
    };

    const openReceiverDetails = (receiver: ReceiverElement) => {
        if (receiver.receiverType === ReceiverType.RECEIVER) {
            setChosenReceiver(receiver.receiver);
            setReceiverDetailsModalIsOpen(true);
        } else {
            setChosenCrossBorderReceiver(receiver.crossBorderReceiver);
            setCrossBorderReceiverModalIsOpen(true);
        }
    };

    const resetReceiverDetails = () => {
        setEditDone(false);
        dispatch({ type: Actions.RESET });
        invalidate();
    };

    const resetCrossBorderReceiverDetails = () => {
        crossBorderReceiverQuery.refetch();
        setEditCrossBorderReceiver(false);
    };

    if (receiverQuery.isError && crossBorderReceiverQuery.isError) {
        return (
            <Content>
                <ErrorCardWrapper
                    title={translated.errors.somethingWentWrongFetchingReceivers}
                    exception={receiverQuery.error}
                >
                    <Button
                        onClick={() => {
                            receiverQuery.refetch();
                            crossBorderReceiverQuery.refetch();
                        }}
                    >
                        {translated.tryAgain}
                    </Button>
                </ErrorCardWrapper>
            </Content>
        );
    }
    return (
        <Content className="receivers__content">
            <InputLabel label={translated.search}>
                <SearchInput
                    placeholder={translated.receiverSearch}
                    value={searchString}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value)}
                    maxLength={35}
                    id="search-input-receivers"
                    className={classNames('select-receiver-form__search-field', {
                        'with-label': filteredReceivers.length === 0,
                    })}
                />
            </InputLabel>
            {!receiverQuery.isFetched ? (
                <span className="receivers__add-new-button receivers__add-new-button__skeleton">
                    <DarkSkeleton height={26} width={190} />
                </span>
            ) : (
                <LinkButton
                    onClick={openAddNewReceiverModal}
                    icon={IconTypes.PROFIL_24}
                    className="receivers__add-new-button"
                >
                    {translated.addNewReceiver}
                </LinkButton>
            )}

            {(receiverQuery.isError || crossBorderReceiverQuery.isError) && (
                <ErrorCard
                    className="fetching-some-receiver-error"
                    title={translated.errors.somethingWentWrong}
                    description={format(
                        translated.errors.somethingWentWrongFetchingSomeReceivers,
                        receiverQuery.isError
                            ? translated.receiverTypes.receiver
                            : translated.receiverTypes.crossBorderReceiver,
                    )}
                />
            )}

            {!receiverQuery.isFetched && !crossBorderReceiverQuery.isFetched ? (
                <ReceiversSkeleton />
            ) : (
                <div className="receivers__list">
                    <SelectList>
                        {filteredReceivers.map((receiver) => (
                            <li key={receiver.id}>
                                <ReceiverListItem receiver={receiver} onClick={() => openReceiverDetails(receiver)} />
                            </li>
                        ))}
                    </SelectList>
                </div>
            )}
            <EikaModal
                id="new-receiver"
                open={newReceiverModalIsOpen}
                setOpen={setNewReceiverModalIsOpen}
                onClose={closeNewReceiverModal}
                timeOut={showSuccess ? 2500 : undefined}
                className="receivers-modal"
            >
                {showSuccess ? (
                    <div className="receiver__success">
                        <CheckmarkAnimation />
                        <h2>{translated.receiverIsAdded}</h2>
                    </div>
                ) : (
                    <>
                        <h2 className="new-receiver-title">{translated.addReceiver}</h2>
                        {featureToggles.enableCreateCrossBorderReceiver ? (
                            <Tabs id="new-receiver-tabs" className="new-receiver-tabs">
                                <Tabs.TabList>
                                    <Tabs.TabButton value={ReceiverTypes.NORWEGIAN}>
                                        {translated.receiverTypes.receiver}
                                    </Tabs.TabButton>
                                    <Tabs.TabButton value={ReceiverTypes.CROSS_BORDER}>
                                        {translated.crossBorderPayment.newReceiver.createNewReceiverTab}
                                    </Tabs.TabButton>
                                </Tabs.TabList>
                                <Tabs.Tab value={ReceiverTypes.NORWEGIAN} className="new-receiver__container">
                                    <NewReceiver
                                        receivers={receivers}
                                        searchString={searchString}
                                        onChange={() => setShowSuccess(true)}
                                        isInPaymentContext={false}
                                    />
                                </Tabs.Tab>
                                <Tabs.Tab value={ReceiverTypes.CROSS_BORDER} className="new-receiver__container">
                                    <CrossBorderFormContext
                                        validateHook={useValidate}
                                        reducer={crossBorderPaymentReducer}
                                        initialState={{ ...crossBorderPaymentInitialState }}
                                    >
                                        <CreateNewCrossBorderReceiver onSuccess={setShowSuccess} />
                                    </CrossBorderFormContext>
                                </Tabs.Tab>
                            </Tabs>
                        ) : (
                            <NewReceiver
                                receivers={receivers}
                                searchString={searchString}
                                onChange={() => setShowSuccess(true)}
                                isInPaymentContext={false}
                            />
                        )}
                    </>
                )}
            </EikaModal>
            <EikaModal
                id="receiver-details"
                open={receiverDetailsModalIsOpen}
                setOpen={setReceiverDetailsModalIsOpen}
                onClose={resetReceiverDetails}
                timeOut={editDone ? 2500 : undefined}
                className="receivers-modal"
            >
                {chosenReceiver && (
                    <ReceiverDetails
                        receiver={chosenReceiver}
                        receivers={receivers}
                        onClose={() => setReceiverDetailsModalIsOpen(false)}
                        setDone={() => setEditDone(true)}
                    />
                )}
            </EikaModal>
            <EikaModal
                id="cross-border-receiver-details"
                open={crossBorderReceiverModalIsOpen}
                setOpen={setCrossBorderReceiverModalIsOpen}
                onClose={resetCrossBorderReceiverDetails}
                timeOut={editCrossBorderReceiver ? 2500 : undefined}
                contentShouldStretch
            >
                <CrossBorderReceiversDetails
                    receiver={chosenCrossBorderReceiver!}
                    onClose={() => setCrossBorderReceiverModalIsOpen(false)}
                    onEdit={() => setEditCrossBorderReceiver(true)}
                />
            </EikaModal>
        </Content>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_RECEIVER}>
        <Receivers />
    </SigningContextProvider>
);
